<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch, type PropType } from "vue";

const props = defineProps({
  open: { default: false, type: Boolean },
  // The full-width row the flyout hangs off. Its right edge is the rail's inner
  // edge in BOTH rail states, so anchoring to the row — instead of to the icon
  // square — keeps the flyout beside the panel whether it is collapsed (4.5rem)
  // or hover-expanded (16rem), with no hard-coded offsets to keep in sync.
  anchor: { default: null, type: Object as PropType<HTMLElement | null> },
});

const cardEl = ref<HTMLElement | null>(null);
const offset = ref({ top: "0px", left: "0px" });
const cardMaxHeight = ref("none");

// Breathing room kept from the top/bottom of the viewport when a long list
// (Help Desk has ~11 children, CRM ~12) has to be pushed up to stay on screen.
const EDGE_GAP = 12;
// The flyout starts level with the row rather than at its top edge, matching
// the old tooltip's -15px lift without letting it drift off the row.
const ROW_LIFT = 8;

const place = async () => {
  const anchor = props.anchor;
  if (!anchor) return;
  const rect = anchor.getBoundingClientRect();
  // Cap the height first: the clamp below has to work with the height the card
  // will actually render at, not the height it would like to be.
  cardMaxHeight.value = `${window.innerHeight - EDGE_GAP * 2}px`;
  offset.value = { top: `${rect.top - ROW_LIFT}px`, left: `${rect.right}px` };
  await nextTick();
  const height = cardEl.value?.offsetHeight ?? 0;
  const top = Math.min(
    Math.max(EDGE_GAP, rect.top - ROW_LIFT),
    Math.max(EDGE_GAP, window.innerHeight - EDGE_GAP - height),
  );
  offset.value = { ...offset.value, top: `${top}px` };
};

// The anchor moves under an open flyout in three ways: the rail's own list
// scrolls independently of the page (hence capture, to catch that inner
// scroll), the window resizes, and — the one that matters most — the row itself
// widens while the panel animates open, because a flyout opened during those
// 200ms would otherwise sit where the collapsed rail used to end and get buried
// under the expanding panel.
let anchorObserver: ResizeObserver | null = null;

const startTracking = () => {
  window.addEventListener("resize", place);
  window.addEventListener("scroll", place, true);
  if (props.anchor && typeof ResizeObserver !== "undefined") {
    anchorObserver = new ResizeObserver(() => place());
    anchorObserver.observe(props.anchor);
  }
};
const stopTracking = () => {
  window.removeEventListener("resize", place);
  window.removeEventListener("scroll", place, true);
  anchorObserver?.disconnect();
  anchorObserver = null;
};

watch(
  () => props.open,
  (open) => {
    if (open) {
      place();
      startTracking();
    } else {
      stopTracking();
    }
  },
);

onBeforeUnmount(stopTracking);
</script>

<template>
  <!-- Fixed, not absolute: the rail's list is `overflow-y: auto`, which would
       clip an absolutely positioned child sideways. A fixed element is only
       clipped by an ancestor that is its containing block (transform / filter /
       contain), and the panel animates `width` precisely so none exists. It
       still lives inside the panel in the DOM, so hovering or focusing the
       flyout counts as being inside the panel and keeps the rail expanded. -->
  <Transition name="flyout">
    <div v-show="props.open" class="side-nav-flyout" :style="offset">
      <div
        ref="cardEl"
        class="side-nav-flyout-card"
        :style="{ maxHeight: cardMaxHeight }"
      >
        <slot />
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.side-nav-flyout {
  position: fixed;
  z-index: 30;
  /* Transparent bridge: the card is inset by this much, so the pointer never
     crosses a gap where neither the row nor the flyout is hovered on its way
     out of the rail. */
  padding-left: 18px;
}

.side-nav-flyout-card {
  max-width: 18rem;
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 0.75rem;
  padding: 0.5rem;
  background-color: var(--light-purple);
  border: 1px solid rgb(255 255 255 / 0.18);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
}

.dark .side-nav-flyout-card {
  background-color: var(--dark-blue);
  border-color: var(--dark-primary-border);
}

.side-nav-flyout-card::-webkit-scrollbar {
  width: 4px;
}

.side-nav-flyout-card::-webkit-scrollbar-thumb {
  background-color: rgb(255 255 255 / 0.18);
  border-radius: 4px;
}

.flyout-enter-active,
.flyout-leave-active {
  transition:
    opacity 120ms ease,
    transform 120ms ease;
}

.flyout-enter-from,
.flyout-leave-to {
  opacity: 0;
  transform: translateX(-4px);
}
</style>
